import { useLibrary, useToggleFollowCreator } from "@/lib/lutely-api/hooks";
import { saveRedirect } from "@/lib/storage-utils";
import { cn } from "@/lib/utils";
import { Button } from "@radix-ui/themes";
import { useNavigate } from "@tanstack/react-router";
import { IoAdd, IoCheckmark } from "react-icons/io5";

export type FollowButtonProps = {
  creatorId: string;
  /** Page theme colors — artist pages pass their theme trio; dark pages pass white/dark. */
  primary: string;
  onPrimary: string;
  primaryInk: string;
  className?: string;
};

/**
 * Follow/Following pill. Outlined with a plus icon while not following;
 * filled with a check once followed (Spotify-style). Optimistic toggle;
 * logged-out taps open the signup modal.
 */
export default function FollowButton({
  creatorId,
  primary,
  onPrimary,
  primaryInk,
  className,
}: FollowButtonProps) {
  const navigate = useNavigate();
  const { isLoggedIn, isCreatorFollowed } = useLibrary();
  const { mutate: toggleFollowCreator } = useToggleFollowCreator();

  const following = isCreatorFollowed(creatorId);

  const handleClick = () => {
    if (!isLoggedIn) {
      saveRedirect(window.location.href);
      navigate({
        to: ".",
        search: (prev) => ({ ...prev, auth: "signup" as const }),
      });
      return;
    }

    toggleFollowCreator({ creatorId });
  };

  return (
    <Button
      variant={following ? "solid" : "outline"}
      onClick={handleClick}
      aria-pressed={following}
      className={cn(
        "flex items-center gap-1.5 flex-shrink-0 text-sm font-semibold leading-[140%] px-4 h-[34px] rounded-full transition-all hover:brightness-90 cursor-pointer",
        className,
      )}
      style={
        following
          ? { backgroundColor: primary, color: onPrimary }
          : { color: primaryInk, boxShadow: `inset 0 0 0 1px ${primaryInk}` }
      }
    >
      {following ? (
        <IoCheckmark className="w-4 h-4" />
      ) : (
        <IoAdd className="w-4 h-4" />
      )}
      {following ? "Following" : "Follow"}
    </Button>
  );
}
